/**
 * Soft!=Live Page198 — Settings Password residual liquid (Page119/61 + dual).
 * Mount: /settings/security
 * Buses (contract ops — never painted in operator DOM):
 *   get__settings_security_status · patch password
 * Soft residual deleted:
 *  - Soft MFA enable / TOTP / passkey CTAs
 *  - Soft password-changed invent without write seat
 *  - Soft rule-pass without draft validation
 *  - Soft invent seats while posture loading
 *  - path /operation labels painted in DOM
 */
import { useState } from "react";
import { html, Ico, Card, Field, Input, Alert, KV, go } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import * as D from "./data.js";
import { useSession } from "./session.js";

/** Path-free operator copy for Password desk. Catalogue stays empty until Live says so. */
export function softPasswordSettingsErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|not_authorized|denied/.test(low)) {
    return "Your account cannot change the password here. Ask a manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/current|incorrect|wrong password|invalid credentials/.test(low)) {
    return "Current password is not correct.";
  }
  if (/\b422\b|\b400\b|invalid|required|policy|weak|rules/.test(low)) {
    return "That password was not accepted. Check the rules and try again.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable/.test(low)) {
    return "Password change is not ready right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/auth\/|\/settings\/security|GET |POST |PATCH |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

/** Soft!=Live Page198: missing contract ops must not crash the desk. */
function safePasswordPath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    if (op === "get__settings_security_status") return "/settings/security/status";
    if (op === "patch__auth_me_password") return "/auth/me/password";
    return "";
  }
}

/** Pure: seat chips from Live Settings Password posture (Page198) — fail-closed. */
export function passwordSettingsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const minLen = Number(lim.password_min_len);
  return {
    passwordChangeAllowed: p.password_change_allowed === true,
    mfaHonestyAllowed: p.mfa_honesty_allowed === true,
    mfaTotpLive: p.mfa_totp_live === true,
    mfaWebauthnLive: p.mfa_webauthn_live === true,
    mfaEnableCtaAllowed: p.mfa_enable_cta_allowed === true,
    dangerNavAllowed: p.danger_nav_allowed === true,
    sessionsNavAllowed: p.sessions_nav_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    postureLabel: String(p.posture_label || "").trim(),
    passwordMinLen: Number.isFinite(minLen) && minLen > 0 ? Math.floor(minLen) : 12,
  };
}

/** Pure: password rule progress. */
export function passwordRulesOkOf(next) {
  const n = String(next || "");
  return (D.PW_RULES || []).every((r) => r && typeof r.test === "function" && r.test(n));
}

export function PasswordSettingsDesk({ toast }) {
  const { me, reload } = useSession();
  const posture = useApi(safePasswordPath("get__settings_security_status"));
  const post = posture.data || {};
  // Fail-closed while loading — seats fail-closed.
  const seat = passwordSettingsSeatOf(posture.loading || !posture.data ? {} : post);
  const { run, busy, error, envelope } = useAction();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const rulesOk = passwordRulesOkOf(next);
  const postureLabel = seat.postureLabel
    || (seat.passwordChangeAllowed
      ? (seat.mfaHonestyAllowed
        ? "Password change open · MFA honesty open"
        : "Password change open")
      : "Password desk sealed");

  const reRead = () => {
    if (!seat.reReadAllowed) {
      toast && toast("Refresh is not available on this seat.", "warn");
      return;
    }
    posture.reload && posture.reload();
    reload && reload();
    toast && toast("Password desk refreshed", "go");
  };

  const save = async () => {
    if (!seat.passwordChangeAllowed) {
      toast && toast("Your seat cannot change the password.", "warn");
      return;
    }
    if (next !== confirm) {
      toast("Confirmation does not match", "bad");
      return;
    }
    if (!rulesOk) {
      toast("Password does not meet the rules", "bad");
      return;
    }
    if (!cur) {
      toast("Current password is required", "bad");
      return;
    }
    const out = await run(safePasswordPath("patch__auth_me_password") || path("patch__auth_me_password"), {
      method: "PATCH",
      body: {
        current_password: cur,
        new_password: next,
        new_password_confirm: confirm,
      },
    });
    if (out) {
      toast("Password changed · other sessions ended", "go");
      setCur("");
      setNext("");
      setConfirm("");
      reload && reload();
    } else toast(softPasswordSettingsErrorOf(error), "bad");
  };

  if (posture.loading && !posture.data) {
    return html`<div className="pw-set-desk pw-set-desk--liquid"
      data-cmc-page="page119-password" data-cmc-password="page61" data-cmc-residual="page198-password">
      <p className="note">Reading password seat… Password change only after the account seat accepts.</p>
    </div>`;
  }

  if (!seat.passwordChangeAllowed && !seat.mfaHonestyAllowed) {
    return html`<div className="pw-set-desk pw-set-desk--liquid"
      data-cmc-page="page119-password" data-cmc-password="page61" data-cmc-residual="page198-password">
      <${Alert} tone="bad" icon="ban" title="Password desk sealed"
        say=${postureLabel + ". Password change only after the account seat accepts."}/>
      <button type="button" className="btn btn--ghost btn--sm"
        disabled=${!seat.reReadAllowed || posture.loading} onClick=${reRead}>
        <${Ico} d="refresh" w=${14}/> Re-read
      </button>
    </div>`;
  }

  return html`<div className="pw-set-desk pw-set-desk--liquid"
    data-cmc-page="page119-password" data-cmc-password="page61" data-cmc-residual="page198-password">
    <div className="pw-set-desk__rail">
      <div>
        <h2 className="pw-set-desk__title">Change password</h2>
      </div>
      <div className="btn-row pw-set-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${!seat.reReadAllowed || posture.loading} onClick=${reRead}>
          <${Ico} d="refresh" w=${14}/> Re-read
        </button>
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${!seat.sessionsNavAllowed}
          onClick=${() => go("/settings/sessions")}>Sessions</button>
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${!seat.dangerNavAllowed}
          onClick=${() => go("/settings/danger")}>Danger zone</button>
      </div>
    </div>

    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <div className="pw-set-desk__kpis" role="group" aria-label="Password posture">
      <div className="pw-set-desk__kpi" data-on=${seat.passwordChangeAllowed ? "1" : "0"}>
        <span>Change</span>
        <b>${seat.passwordChangeAllowed ? "open" : "sealed"}</b>
        <em>password</em>
      </div>
      <div className="pw-set-desk__kpi" data-on=${seat.mfaHonestyAllowed ? "1" : "0"}>
        <span>MFA honesty</span>
        <b>${seat.mfaHonestyAllowed ? "open" : "sealed"}</b>
        <em>read-only</em>
      </div>
      <div className="pw-set-desk__kpi" data-on=${seat.mfaTotpLive ? "1" : "0"}>
        <span>Authenticator</span>
        <b>${seat.mfaTotpLive ? "on" : "off"}</b>
        <em>no Soft CTA</em>
      </div>
      <div className="pw-set-desk__kpi" data-on="0">
        <span>Seat</span>
        <b>${seat.role || (me && me.role) || "—"}</b>
        <em>min ${seat.passwordMinLen}</em>
      </div>
    </div>
    ${posture.error ? html`
      <${Alert} tone="warn" title="Could not refresh password status"
        say=${softPasswordSettingsErrorOf(posture.error) + " Password change only after the account seat accepts."}/>` : null}

    <${Card} title="Change your password">
      ${!seat.passwordChangeAllowed ? html`
        <${Alert} tone="info" title="Password change sealed"
          say="Change recorded only after the server accepts it."/>` : null}
      <${Field} label="Current password">
        <${Input} icon="lock" type="password" value=${cur}
          disabled=${!seat.passwordChangeAllowed}
          onInput=${(e) => setCur(e.target.value)} placeholder="••••••••••••"
          autocomplete="current-password"/><//>
      <${Field} label="New password">
        <${Input} icon="lock" type="password" value=${next}
          disabled=${!seat.passwordChangeAllowed}
          onInput=${(e) => setNext(e.target.value)} placeholder=${"At least " + seat.passwordMinLen + " characters"}
          autocomplete="new-password"/><//>
      <${Field} label="Confirm">
        <${Input} icon="lock" type="password" value=${confirm}
          disabled=${!seat.passwordChangeAllowed}
          onInput=${(e) => setConfirm(e.target.value)} placeholder="Type it again"
          autocomplete="new-password"/><//>
      <${Alert} tone="warn" title="This ends every other session"
        say="The account session revision is bumped. This browser stays signed in; every other device must sign in again."/>
      <div className="pw-grid">
        ${(D.PW_RULES || []).map((r) => html`
          <span key=${r.id} className="pw-rule" data-ok=${r.test(next) ? "1" : "0"}>
            <${Ico} d=${r.test(next) ? "check" : "minus"} w=${11}/>${r.say}
          </span>`)}
      </div>
      <div className="btn-row" style=${{ marginTop: 16, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,.07)" }}>
        <button type="button" className="btn btn--sm btn--go"
          disabled=${busy || !cur || !next || !rulesOk || next !== confirm || !seat.passwordChangeAllowed}
          onClick=${save}>
          ${busy ? "Saving…" : "Change password"}
        </button>
      </div>
      ${me ? html`<p className="note" style=${{ marginTop: 10 }}>Signed in as <b>${me.username}</b> · ${me.role || "—"}</p>` : null}
    <//>

    ${seat.mfaHonestyAllowed ? html`
      <${Card} title="Second factor (honesty)"
          sub="Not inventable here — this surface does not enable MFA.">
        <div className="verdict" data-ok="0">
          <span className="verdict__st"><${Ico} d="ban" w=${13}/></span>
          <span className="txt">
            <b style=${{ fontFamily: "var(--sans)", fontWeight: 500 }}>Authenticator / passkeys</b>
            <span>
              ${seat.mfaTotpLive || seat.mfaWebauthnLive
                ? "A live factor is present on this deployment."
                : "Not implemented on this deployment (no authenticator, no passkey). Not offered until this site implements it."}
            </span>
          </span>
        </div>
        ${seat.mfaEnableCtaAllowed ? null : html`
          <p className="note">
            Email second factor for staff is a deployment setting. Password re-verification is
            required before account closure. MFA enable is not on this page.
          </p>`}
      <//>` : html`
      <${Alert} tone="info" title="MFA honesty sealed"
        say="No MFA pamphlet invented here."/>`}
  </div>`;
}
